/**
 * Raiz do roteador — React Router 7 em Data Mode.
 *
 * As rotas nascem de `rotas.ts`, que é o porte de `app/routes.js`: uma tabela,
 * um roteador. Cada surface tem uma rota de layout **sem caminho** e os filhos
 * usam caminho absoluto, porque a impersonação vive em `/admin/impersonar/…` e
 * roda no shell da clínica — com layout ancorado em `/app` ela não teria como
 * ser filha dele.
 */

import { Outlet, createBrowserRouter, redirect } from 'react-router';
import type { LoaderFunctionArgs, RouteObject, ShouldRevalidateFunctionArgs } from 'react-router';
import type { ReactElement } from 'react';

import { ProvedorDeAvisos } from '@/comuns/toast';
import { Entrar } from '@/screens/Entrar';
import { CriarConta } from '@/screens/CriarConta';
import { RecuperarSenha } from '@/screens/RecuperarSenha';
import { Agenda, carregarAgenda } from '@/screens/Agenda';
import { AtendimentoEditor, carregarAtendimentoEditor } from '@/screens/AtendimentoEditor';
import { Atendimentos, carregarAtendimentos } from '@/screens/Atendimentos';
import { Comunicacao, carregarComunicacao } from '@/screens/Comunicacao';
import { Contatos, carregarContatos } from '@/screens/Contatos';
import { Crm, carregarCrm } from '@/screens/Crm';
import { Fichas, carregarFichas } from '@/screens/Fichas';
import { Compras, carregarCompras } from '@/screens/Compras';
import { Documentos, carregarDocumentos } from '@/screens/Documentos';
import { Relatorios, carregarRelatorios } from '@/screens/Relatorios';
import { Configuracoes, carregarConfiguracoes } from '@/screens/Configuracoes';
import { Automacoes, carregarAutomacoes } from '@/screens/Automacoes';
import { Marketing, carregarMarketing } from '@/screens/Marketing';
import { ApiDaClinica } from '@/screens/ApiDaClinica';
import { AgentesEIa, carregarAgentes } from '@/screens/AgentesEIa';
import { Brain, carregarBrain } from '@/screens/Brain';
import { Visao, carregarPlataforma } from '@/screens/admin/Visao';
import { Clinicas, carregarClinicas } from '@/screens/admin/Clinicas';
import { Clinica, carregarClinicaDaPlataforma } from '@/screens/admin/Clinica';
import { Usuarios } from '@/screens/admin/Usuarios';
import { Auditoria } from '@/screens/admin/Auditoria';
import { Saude } from '@/screens/admin/Saude';
import { Planos, carregarFaturamento } from '@/screens/admin/Planos';
import { Faturamento } from '@/screens/admin/Faturamento';
import { Metricas, carregarMetricas } from '@/screens/admin/Metricas';
import { Onboarding, carregarOnboarding } from '@/screens/admin/Onboarding';
import { Suporte, carregarSuporte } from '@/screens/admin/Suporte';
import { Flags, carregarFlags } from '@/screens/admin/Flags';
import { IntegracoesDaPlataforma, carregarChavesDeIa } from '@/screens/admin/IntegracoesDaPlataforma';
import { ConsumoIa, carregarConsumoDeIa } from '@/screens/admin/ConsumoIa';
import { Estoque, carregarEstoque } from '@/screens/Estoque';
import { Procedimentos, carregarProcedimentos } from '@/screens/Procedimentos';
import { PlanosDeSaude, carregarPlanosDeSaude } from '@/screens/PlanosDeSaude';
import { Financeiro, carregarFinanceiro } from '@/screens/Financeiro';
import { Vendas, carregarVendas } from '@/screens/Vendas';
import { Paciente, carregarPaciente } from '@/screens/Paciente';
import { Inicio, carregarInicio } from '@/screens/Inicio';
import { TelaPendente } from '@/screens/TelaPendente';
import { DocumentoLegalTela } from '@/screens/DocumentoLegal';
import { LISTA_DE_DOCUMENTOS } from '@/data/juridico';
import { AuthShell } from '@/shells/AuthShell';
import { ClinicShell } from '@/shells/ClinicShell';
import { PlatformShell } from '@/shells/PlatformShell';
import { atual } from '@/session/sessao';
import type { Surface } from '@/session/sessao';
import { ErroDeRota } from './ErroDeRota';
import { baseDaSessao, loaderDaRota } from './guardas';
import { rotas } from './rotas';
import type { Rota } from './rotas';
import type { HandleDeRota } from './useRotaAtual';

/**
 * As telas já portadas, por id de rota. O que não está aqui monta o marca-lugar,
 * então portar uma tela é acrescentar uma linha — o roteador não muda.
 */
interface TelaPortada {
    elemento: ReactElement;
    /** Carga da tela. Roda depois das guardas, nunca antes. */
    carregar?: (args: LoaderFunctionArgs) => Promise<unknown>;
}

const TELAS: Record<string, TelaPortada> = {
    entrar: { elemento: <Entrar /> },
    'criar-conta': { elemento: <CriarConta /> },
    'recuperar-senha': { elemento: <RecuperarSenha /> },
    inicio: { elemento: <Inicio />, carregar: carregarInicio },
    agenda: { elemento: <Agenda />, carregar: carregarAgenda },
    atendimentos: { elemento: <Atendimentos />, carregar: carregarAtendimentos },
    'atendimento-editor': { elemento: <AtendimentoEditor />, carregar: carregarAtendimentoEditor },
    comunicacao: { elemento: <Comunicacao />, carregar: carregarComunicacao },
    contatos: { elemento: <Contatos />, carregar: carregarContatos },
    crm: { elemento: <Crm />, carregar: carregarCrm },
    'ficha-builder': { elemento: <Fichas />, carregar: carregarFichas },
    compras: { elemento: <Compras />, carregar: carregarCompras },
    documentos: { elemento: <Documentos />, carregar: carregarDocumentos },
    relatorios: { elemento: <Relatorios />, carregar: carregarRelatorios },
    configuracoes: { elemento: <Configuracoes />, carregar: carregarConfiguracoes },
    automacoes: { elemento: <Automacoes />, carregar: carregarAutomacoes },
    marketing: { elemento: <Marketing />, carregar: carregarMarketing },
    api: { elemento: <ApiDaClinica /> },
    agentes: { elemento: <AgentesEIa />, carregar: carregarAgentes },
    /* ---- trilha B4: brain ---- */
    brain: { elemento: <Brain />, carregar: carregarBrain },

    /* Painel da plataforma, completo. */
    visao: { elemento: <Visao />, carregar: carregarPlataforma },
    clinicas: { elemento: <Clinicas />, carregar: carregarClinicas },
    clinica: { elemento: <Clinica />, carregar: carregarClinicaDaPlataforma },
    usuarios: { elemento: <Usuarios />, carregar: carregarPlataforma },
    auditoria: { elemento: <Auditoria />, carregar: carregarPlataforma },
    saude: { elemento: <Saude />, carregar: carregarPlataforma },
    planos: { elemento: <Planos />, carregar: carregarFaturamento },
    faturamento: { elemento: <Faturamento />, carregar: carregarFaturamento },
    metricas: { elemento: <Metricas />, carregar: carregarMetricas },
    onboarding: { elemento: <Onboarding />, carregar: carregarOnboarding },
    suporte: { elemento: <Suporte />, carregar: carregarSuporte },
    flags: { elemento: <Flags />, carregar: carregarFlags },
    integracoes: { elemento: <IntegracoesDaPlataforma />, carregar: carregarChavesDeIa },
    ia: { elemento: <ConsumoIa />, carregar: carregarConsumoDeIa },
    /* A rota da impersonação mostra o INÍCIO da clínica visitada — é o que o
       protótipo declarava, e é o que faz sentido: já se está dentro. Quem abre
       a impersonação é o clique no modal da ficha, porque começar uma é
       escrita, e escrita não acontece por navegação. */
    impersonar: { elemento: <Inicio />, carregar: carregarInicio },
    estoque: { elemento: <Estoque />, carregar: carregarEstoque },
    procedimentos: { elemento: <Procedimentos />, carregar: carregarProcedimentos },
    'planos-de-saude': { elemento: <PlanosDeSaude />, carregar: carregarPlanosDeSaude },
    financeiro: { elemento: <Financeiro />, carregar: carregarFinanceiro },
    vendas: { elemento: <Vendas />, carregar: carregarVendas },
    paciente: { elemento: <Paciente />, carregar: carregarPaciente },
};

function elementoDa(rota: Rota) {
    return TELAS[rota.id]?.elemento ?? <TelaPendente rota={rota} />;
}

/**
 * Guardas primeiro, dados depois. Buscar antes de autorizar entregaria a
 * consulta a quem a guarda ia recusar — e o custo já teria sido pago.
 */
function loaderDe(rota: Rota) {
    const guarda = loaderDaRota(rota);
    const carregar = TELAS[rota.id]?.carregar;
    if (!carregar) return guarda;
    return async (args: LoaderFunctionArgs) => {
        guarda(args);
        return carregar(args);
    };
}

function rotasDaSurface(surface: Surface): RouteObject[] {
    return rotas
        .filter((r) => r.surface === surface)
        .map((r) => ({
            path: r.path,
            loader: loaderDe(r),
            /* Sem isto, trocar de subrota (a aba de Contatos, por exemplo) não
               recarrega: o React Router só revalida o loader do trecho da URL
               que casou com ESTA rota, e a aba vive no trecho do filho. A tela
               continuava mostrando a lista anterior com a URL nova. */
            shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) =>
                defaultShouldRevalidate
                || currentUrl.pathname !== nextUrl.pathname
                || currentUrl.search !== nextUrl.search,
            handle: { rota: r } satisfies HandleDeRota,
            element: elementoDa(r),
            children: r.children.map((c) => ({
                path: c.path,
                handle: { rota: r, subrota: c } satisfies HandleDeRota,
                element: <TelaPendente rota={r} subrota={c} />,
            })),
        }));
}

export const rotasDoRoteador: RouteObject[] = [
    {
        /* Os avisos ficam acima do roteador: a tela de entrar também avisa, e
           um provedor por shell perderia o aviso na troca de surface. */
        element: <ProvedorDeAvisos><Outlet /></ProvedorDeAvisos>,
        errorElement: <ErroDeRota />,
        children: [
            {
                /* A raiz não é tela: ela decide para onde a sessão pode ir. */
                index: true,
                loader: () => redirect(atual() ? baseDaSessao() : '/entrar'),
                /* Sempre redireciona; o elemento existe para o React Router não
                   avisar de página vazia — e para dizer algo se o redirecionamento
                   demorar um quadro. */
                element: <p style={{ padding: 24, fontFamily: 'var(--font-ui)', color: 'var(--text-secondary)' }}>Redirecionando…</p>,
            },
            { element: <AuthShell />, children: rotasDaSurface('auth') },
            { element: <ClinicShell />, children: rotasDaSurface('clinic') },
            { element: <PlatformShell />, children: rotasDaSurface('platform') },
            /* Termos, Privacidade e LGPD: públicos e fora das surfaces. A surface auth manda quem já
               entrou para o app, e estas páginas abrem com ou sem sessão — a verificação do Google e o
               aceite do cadastro apontam para elas. Não são telas do protótipo: não entram em app/routes.js. */
            ...LISTA_DE_DOCUMENTOS.map((d): RouteObject => ({ path: d.caminho, element: <DocumentoLegalTela id={d.id} /> })),
            {
                /* 404 como resposta, e não como tela solta: assim ele cai na
                   mesma fronteira de erro que o 403 e a falha inesperada. */
                path: '*',
                loader: () => {
                    throw new Response('Rota inexistente.', { status: 404, statusText: 'Não encontrada' });
                },
                /* O loader sempre lança, então este elemento não chega a
                   renderizar — ele existe porque rota sem elemento faz o React
                   Router avisar que a página ficaria vazia. */
                element: <ErroDeRota />,
            },
        ],
    },
];

/**
 * Cria o roteador — e é uma FUNÇÃO de propósito.
 *
 * `createBrowserRouter` dispara o loader da URL atual **na criação**, não na
 * montagem. Como `const` no topo do módulo, ele rodava assim que o import fosse
 * avaliado — antes de `inicializarSessao()` terminar. A guarda via a sessão
 * vazia e mandava para `/entrar` quem tinha token válido guardado: abrir um
 * link fundo, ou dar F5, deslogava. O `main.tsx` já esperava a sessão; o que
 * não esperava era o grafo de imports.
 */
export function criarRoteador() {
    return createBrowserRouter(rotasDoRoteador);
}
